/**
 * Manager Mode phase panel — phase results, stats, PL table, leaderboards,
 * transfer builder (max 5) and captain pick for the next phase.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    ArrowRight, CaretDown, CheckCircle, Fire, ListNumbers, ShieldCheck, Swap, TrendDown, Trophy, UsersThree, X,
} from "@phosphor-icons/react";

const TIMING_API = `${process.env.REACT_APP_BACKEND_URL}/api/timing`;

import ManagerShareCard from "./ManagerShareCard";

const PHASE_LABELS = {
    gw1_6: "Start (GW1–6)", gw7_12: "První třetina (GW7–12)", gw13_18: "Před Vánoci (GW13–18)",
    gw19_24: "Vánoce (GW19–24)", gw25_30: "Před finišem (GW25–30)", gw31_38: "Run-in (GW31–38)",
};
const BOARD_TITLES = { GKP: "Top 5 GK", DEF: "Top 10 DEF", MID: "Top 10 MID", FWD: "Top 6 FWD" };

export default function ManagerPhasePanel({ report, pool, busy, onNext, onReset }) {
    const [transfers, setTransfers] = useState([]); // [{out_id,in_id}]
    const [captainId, setCaptainId] = useState(report.captain_id || null);
    const [showTable, setShowTable] = useState(false);

    const squadIds = useMemo(() => new Set(report.squad.map((s) => s.fpl_id)), [report]);
    const outIds = new Set(transfers.map((t) => t.out_id));
    const inIds = new Set(transfers.map((t) => t.in_id));

    // Full FPL market — transfers are not limited to the watchlist pool.
    const [allPlayers, setAllPlayers] = useState([]);
    useEffect(() => {
        axios.get(`${TIMING_API}/pool/current-fpl`)
            .then((r) => setAllPlayers((r.data?.players || [])
                .filter((p) => p.fpl_id && p.position)
                .map((p) => ({
                    fpl_id: p.fpl_id,
                    display_name: p.web_name || p.full_name,
                    full_name: p.full_name || p.web_name,
                    position: p.position,
                    team: p.team,
                    current_price: p.price ?? 4.5,
                }))))
            .catch(() => {});
    }, []);
    const poolIds = useMemo(() => new Set(pool.map((p) => p.fpl_id)), [pool]);
    const marketById = useMemo(() => {
        const m = new Map();
        for (const p of allPlayers) m.set(p.fpl_id, p);
        for (const p of pool) m.set(p.fpl_id, p);
        return m;
    }, [allPlayers, pool]);
    const market = useMemo(() => [...marketById.values()], [marketById]);

    const nextSquad = useMemo(() => {
        const m = new Map(report.squad.map((s) => [s.fpl_id, s]));
        for (const t of transfers) {
            const inP = marketById.get(t.in_id);
            if (inP && m.has(t.out_id)) {
                m.delete(t.out_id);
                m.set(inP.fpl_id, { fpl_id: inP.fpl_id, name: inP.display_name, position: inP.position, team: inP.team, price: inP.current_price ?? 4.5 });
            }
        }
        return [...m.values()];
    }, [report, transfers, marketById]);
    const nextCost = nextSquad.reduce((s, p) => s + (p.price ?? 4.5), 0);
    const overBudget = nextCost > report.budget + 0.05;

    const addTransfer = (outId, inId) => {
        if (transfers.length >= report.transfers_allowed) { toast.error(`Max ${report.transfers_allowed} přestupů`); return; }
        setTransfers([...transfers, { out_id: outId, in_id: inId }]);
        if (captainId === outId) setCaptainId(null);
    };

    const phaseLabel = PHASE_LABELS[report.phase.key] || report.phase.key;

    return (
        <div className="space-y-4 border-2 border-fa-mint/40 bg-fa-mint/[0.02] p-4" data-testid="manager-panel">
            {/* Header */}
            <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-fa-mint font-bold">Manažerský režim</span>
                <span className="font-display font-black text-xl uppercase">{phaseLabel}</span>
                <span className="font-mono text-[10px] text-white/50">fáze {report.phase.index + 1}/{report.phase.total_phases}</span>
                <span className="ml-auto flex items-center gap-4 font-mono text-[11px] tabular-nums">
                    <span>Fáze: <strong className="text-fa-yellow">{report.phase_points}</strong> b</span>
                    <span>Celkem: <strong className="text-fa-mint">{report.total_points}</strong> b</span>
                    <span>OR: <strong className="text-fa-sky">{report.overall_rank?.toLocaleString("cs")}</strong></span>
                    <button type="button" onClick={onReset} data-testid="manager-reset"
                            className="text-red-300/70 hover:text-red-300 border border-red-300/30 px-2 py-0.5 text-[9px] uppercase tracking-widest">
                        <X size={10} weight="bold" className="inline mb-0.5" /> Ukončit
                    </button>
                </span>
            </div>

            {/* GW results */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-1.5" data-testid="manager-gw-results">
                {report.gw_reports.map((g) => (
                    <div key={g.gw} className="border border-white/10 bg-black/40 p-2">
                        <div className="font-mono text-[8px] uppercase tracking-widest text-white/40">GW{g.gw}</div>
                        <div className="font-display font-black text-lg tabular-nums text-fa-yellow">{g.points}</div>
                        <div className="font-mono text-[9px] text-white/55 truncate" title={`Kapitán: ${g.captain.name}`}>
                            C: {g.captain.name}
                            {g.captain.changed_by_ai && <span className="text-orange-300" title="AI změnila kapitána (tvůj měl výrazně horší předpověď)"> ⚠AI</span>}
                            {g.captain.vice_used && <span className="text-fa-sky" title="Kapitán nehrál — zaskočil vice"> (V)</span>}
                        </div>
                        {g.autosubs.length > 0 && (
                            <div className="font-mono text-[8px] text-fa-sky/80">{g.autosubs.length}× autosub</div>
                        )}
                    </div>
                ))}
            </div>

            {/* AI transfers */}
            {report.ai_transfers.length > 0 && (
                <div className="font-mono text-[10px] text-white/60 border border-white/10 bg-black/30 p-2" data-testid="manager-ai-transfers">
                    <span className="text-fa-sky uppercase tracking-widest text-[9px] mr-2"><Swap size={10} weight="bold" className="inline mb-0.5" /> AI přestupy ve fázi:</span>
                    {report.ai_transfers.map((t, i) => (
                        <span key={i} className="mr-3">GW{t.gw}: <span className="text-red-300">{t.out}</span> → <span className="text-fa-mint">{t.in}</span></span>
                    ))}
                </div>
            )}

            {/* My squad — every player's phase performance */}
            {report.squad_stats?.length > 0 && (
                <div className="border border-white/10 bg-black/30" data-testid="manager-squad-stats">
                    <div className="flex items-center gap-2 p-2 font-mono text-[9px] uppercase tracking-widest text-fa-mint font-bold">
                        <UsersThree size={11} weight="fill" /> Moje sestava — výsledky fáze
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full font-mono text-[10px]">
                            <thead>
                                <tr className="text-white/40 uppercase tracking-widest text-[8px] border-b border-white/10">
                                    <th className="text-left px-2 py-1">Hráč</th>
                                    <th className="text-left px-2 py-1">Tým</th>
                                    <th className="text-right px-2 py-1">£</th>
                                    <th className="text-right px-2 py-1">Body fáze</th>
                                    <th className="text-right px-2 py-1">Oček. fáze</th>
                                    <th className="text-right px-2 py-1">+/−</th>
                                    <th className="text-right px-2 py-1">Sezóna</th>
                                </tr>
                            </thead>
                            <tbody>
                                {report.squad_stats.map((s, i) => {
                                    const prevPos = i > 0 ? report.squad_stats[i - 1].position : null;
                                    return (
                                        <tr key={s.fpl_id} data-testid={`squad-stat-${s.fpl_id}`}
                                            className={`border-b border-white/5 last:border-0 ${prevPos && prevPos !== s.position ? "border-t border-t-white/15" : ""}`}>
                                            <td className="px-2 py-1 text-white/85 whitespace-nowrap">
                                                <span className="text-white/35 text-[8px] mr-1.5">{s.position}</span>
                                                {s.name}
                                                {report.captain_id === s.fpl_id && (
                                                    <span className="ml-1.5 px-1 text-[8px] bg-fa-yellow text-black font-bold" title="Kapitán">C</span>
                                                )}
                                            </td>
                                            <td className="px-2 py-1 text-white/45 whitespace-nowrap">{s.team}</td>
                                            <td className="px-2 py-1 text-right tabular-nums text-white/45">{s.price?.toFixed(1)}</td>
                                            <td className="px-2 py-1 text-right tabular-nums font-bold text-fa-yellow">{s.phase_points}</td>
                                            <td className="px-2 py-1 text-right tabular-nums text-white/55">{s.phase_expected}</td>
                                            <td className={`px-2 py-1 text-right tabular-nums ${s.delta >= 0 ? "text-fa-mint" : "text-red-300"}`}>
                                                {s.delta > 0 ? "+" : ""}{s.delta}
                                            </td>
                                            <td className="px-2 py-1 text-right tabular-nums text-fa-sky">{s.season_points}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Top / Flop */}
            <div className="grid md:grid-cols-2 gap-3">
                <StatList title="Komu se dařilo" icon={Fire} accent="text-fa-mint" rows={report.top_players}
                          value={(s) => `${s.phase_points} b`} testid="manager-top" squadIds={squadIds} />
                <StatList title="Zklamání fáze" icon={TrendDown} accent="text-red-300" rows={report.flop_players}
                          value={(s) => `${s.phase_points} b (${s.delta > 0 ? "+" : ""}${s.delta} vs oček.)`} testid="manager-flops" squadIds={squadIds} />
            </div>

            {/* Leaderboards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="manager-leaderboards">
                {["GKP", "DEF", "MID", "FWD"].map((pos) => (
                    <div key={pos} className="border border-white/10 bg-black/30 p-2">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-fa-yellow mb-1 flex items-center gap-1">
                            <Trophy size={10} weight="fill" /> {BOARD_TITLES[pos]}
                        </div>
                        {(report.leaderboards[pos] || []).map((s, i) => (
                            <div key={s.fpl_id} className="flex justify-between font-mono text-[10px] py-0.5 border-b border-white/5 last:border-0">
                                <span className={`truncate ${s.in_squad ? "text-fa-mint" : "text-white/70"}`}>{i + 1}. {s.name}</span>
                                <span className="tabular-nums text-white/60">{s.season_points}</span>
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            {/* PL table (collapsible) */}
            <div className="border border-white/10 bg-black/30">
                <button type="button" onClick={() => setShowTable(!showTable)} data-testid="manager-table-toggle"
                        className="w-full flex items-center gap-2 p-2 font-mono text-[10px] uppercase tracking-widest text-white/60 hover:text-white">
                    <ListNumbers size={11} weight="bold" className="text-fa-sky" />
                    Fiktivní tabulka Premier League (po GW{report.phase.hi})
                    <CaretDown size={10} weight="bold" className={`ml-auto transition-transform ${showTable ? "rotate-180" : ""}`} />
                </button>
                {showTable && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 p-2 pt-0" data-testid="manager-table">
                        {report.table.map((t) => (
                            <div key={t.team} className="flex justify-between font-mono text-[10px] py-0.5 border-b border-white/5">
                                <span className={t.pos <= 4 ? "text-fa-mint" : t.pos >= 18 ? "text-red-300" : "text-white/70"}>
                                    {t.pos}. {t.short} <span className="text-white/30">{t.team}</span>
                                </span>
                                <span className="tabular-nums text-white/60">{t.w}-{t.d}-{t.l} · {t.gd > 0 ? "+" : ""}{t.gd} · <strong className="text-white">{t.pts}</strong></span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Next phase: transfers + captain */}
            {!report.finished ? (
                <div className="border border-fa-yellow/30 bg-fa-yellow/[0.03] p-3 space-y-3" data-testid="manager-next-controls">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow font-bold">
                        Příprava na {PHASE_LABELS[report.next_phase.key]} · přestupy {transfers.length}/{report.transfers_allowed}
                        <span className={`ml-3 ${overBudget ? "text-red-300" : "text-fa-mint"}`}>£{nextCost.toFixed(1)} / £{report.budget}m</span>
                    </div>
                    {transfers.length > 0 && (
                        <div className="font-mono text-[9px] uppercase tracking-widest text-fa-mint flex items-center gap-1">
                            <CheckCircle size={11} weight="fill" /> Uložené přestupy pro další fázi:
                        </div>
                    )}
                    {transfers.map((t, i) => {
                        const outP = report.squad.find((s) => s.fpl_id === t.out_id);
                        const inP = marketById.get(t.in_id);
                        return (
                            <div key={i} className="flex items-center gap-2 font-mono text-[11px]">
                                <span className="text-red-300">{outP?.name}</span>
                                <ArrowRight size={11} weight="bold" className="text-white/40" />
                                <span className="text-fa-mint">{inP?.display_name}</span>
                                {inP && !poolIds.has(inP.fpl_id) && (
                                    <span className="font-mono text-[8px] uppercase tracking-widest px-1 py-0.5 border border-fa-sky/40 text-fa-sky">mimo pool</span>
                                )}
                                <button type="button" onClick={() => setTransfers(transfers.filter((_, j) => j !== i))}
                                        data-testid={`manager-remove-transfer-${i}`}
                                        className="text-white/40 hover:text-red-300"><X size={11} weight="bold" /></button>
                            </div>
                        );
                    })}
                    {transfers.length < report.transfers_allowed && (
                        <TransferBuilder squad={report.squad.filter((s) => !outIds.has(s.fpl_id))}
                                         market={market.filter((p) => !squadIds.has(p.fpl_id) && !inIds.has(p.fpl_id))}
                                         poolIds={poolIds}
                                         onAdd={addTransfer} />
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1">
                            <ShieldCheck size={11} weight="fill" className="text-fa-yellow" /> Kapitán na fázi:
                        </span>
                        <select value={captainId ?? ""} onChange={(e) => setCaptainId(e.target.value ? parseInt(e.target.value, 10) : null)}
                                data-testid="manager-captain-select"
                                className="bg-black/40 border border-white/15 px-2 py-1 font-mono text-[11px] focus:outline-none focus:border-fa-yellow">
                            <option value="">— nech na AI —</option>
                            {nextSquad.filter((p) => p.position !== "GKP").map((p) => (
                                <option key={p.fpl_id} value={p.fpl_id}>{p.name} ({p.position})</option>
                            ))}
                        </select>
                        <button type="button" disabled={busy || overBudget}
                                onClick={() => onNext(transfers, captainId,
                                    transfers.map((t) => marketById.get(t.in_id)).filter((p) => p && !poolIds.has(p.fpl_id)))}
                                data-testid="manager-next-btn"
                                className="ml-auto font-mono text-[11px] uppercase tracking-widest px-4 py-2 bg-fa-mint text-black font-bold hover:bg-fa-mint/80 disabled:opacity-40">
                            {busy ? "Simuluji…" : `Odsimulovat ${PHASE_LABELS[report.next_phase.key]}`}
                        </button>
                    </div>
                    <p className="font-mono text-[9px] text-white/40 leading-relaxed">
                        AI během fáze smí max 2 přestupy a kapitána změní jen pokud tvůj výběr výrazně zaostává za předpovědí (respektuje tvé přání).
                    </p>
                </div>
            ) : (
                <div className="border border-fa-mint/40 bg-fa-mint/[0.05] p-4 space-y-4" data-testid="manager-finished">
                    <div className="text-center space-y-2">
                        <div className="font-display font-black text-2xl uppercase">Sezóna dohrána 🏁</div>
                        <div className="font-mono text-[12px] tabular-nums">
                            Celkem <strong className="text-fa-yellow">{report.total_points}</strong> bodů ·
                            finální OR <strong className="text-fa-sky"> {report.overall_rank?.toLocaleString("cs")}</strong>
                        </div>
                        <button type="button" onClick={onReset} data-testid="manager-new-season"
                                className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-fa-mint/50 text-fa-mint hover:bg-fa-mint hover:text-black">
                            Nová sezóna
                        </button>
                    </div>
                    <ManagerShareCard report={report} />
                </div>
            )}
        </div>
    );
}

function StatList({ title, icon: Icon, accent, rows, value, testid, squadIds }) {
    return (
        <div className="border border-white/10 bg-black/30 p-2" data-testid={testid}>
            <div className={`font-mono text-[9px] uppercase tracking-widest mb-1 flex items-center gap-1 ${accent}`}>
                <Icon size={10} weight="fill" /> {title}
            </div>
            {rows.map((s) => (
                <div key={s.fpl_id} className="flex justify-between font-mono text-[10px] py-0.5 border-b border-white/5 last:border-0">
                    <span className={`truncate ${squadIds.has(s.fpl_id) ? "text-fa-mint" : "text-white/70"}`}>
                        {s.name} <span className="text-white/30 text-[8px]">{s.position}</span>
                        {squadIds.has(s.fpl_id) && <span className="text-[8px] text-fa-mint/70"> · tvůj</span>}
                    </span>
                    <span className="tabular-nums text-white/60">{value(s)}</span>
                </div>
            ))}
        </div>
    );
}

function TransferBuilder({ squad, market, poolIds, onAdd }) {
    const [outId, setOutId] = useState("");
    const [inId, setInId] = useState("");
    const [q, setQ] = useState("");
    const outP = squad.find((s) => s.fpl_id === parseInt(outId, 10));
    const norm = (s) => (s || "").toLowerCase();
    const candidates = useMemo(() => {
        if (!outP) return [];
        let c = market.filter((p) => p.position === outP.position);
        if (q.trim()) {
            const qq = norm(q);
            c = c.filter((p) => norm(p.display_name).includes(qq) || norm(p.full_name).includes(qq) || norm(p.team).includes(qq));
        }
        c.sort((a, b) => ((poolIds.has(b.fpl_id) ? 1 : 0) - (poolIds.has(a.fpl_id) ? 1 : 0))
            || ((b.current_price ?? 0) - (a.current_price ?? 0)));
        return c.slice(0, 60);
    }, [market, outP, q, poolIds]);
    return (
        <div className="flex items-center gap-2 flex-wrap font-mono text-[11px]" data-testid="manager-transfer-builder">
            <span className="text-white/50 text-[10px] uppercase tracking-widest">Přestup:</span>
            <select value={outId} onChange={(e) => { setOutId(e.target.value); setInId(""); setQ(""); }} data-testid="manager-transfer-out"
                    className="bg-black/40 border border-white/15 px-2 py-1 focus:outline-none focus:border-red-300">
                <option value="">— ven —</option>
                {squad.map((s) => <option key={s.fpl_id} value={s.fpl_id}>{s.name} ({s.position} · £{s.price})</option>)}
            </select>
            {outP && (
                <>
                    <input type="text" value={q} onChange={(e) => { setQ(e.target.value); setInId(""); }}
                           placeholder="Hledej hráče / tým…" data-testid="manager-transfer-search"
                           className="bg-black/40 border border-white/15 px-2 py-1 w-44 focus:outline-none focus:border-fa-mint placeholder:text-white/25" />
                    <select value={inId} data-testid="manager-transfer-in"
                            onChange={(e) => setInId(e.target.value)}
                            className="bg-black/40 border border-fa-mint/40 px-2 py-1 max-w-[280px] focus:outline-none focus:border-fa-mint">
                        <option value="">— dovnitř ({candidates.length}) —</option>
                        {candidates.map((p) => (
                            <option key={p.fpl_id} value={p.fpl_id}>
                                {poolIds.has(p.fpl_id) ? "★ " : ""}{p.display_name} ({p.team} · £{p.current_price ?? 4.5})
                            </option>
                        ))}
                    </select>
                </>
            )}
            <button type="button" disabled={!outP || !inId} data-testid="manager-save-transfer"
                    onClick={() => { onAdd(outP.fpl_id, parseInt(inId, 10)); setOutId(""); setInId(""); setQ(""); }}
                    className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 bg-fa-mint text-black font-bold hover:bg-fa-mint/80 disabled:opacity-30 disabled:cursor-not-allowed">
                <CheckCircle size={11} weight="fill" className="inline mb-0.5 mr-1" /> Uložit přestup
            </button>
            <span className="text-white/30 text-[9px]">★ = z tvého poolu · vybírat lze z celé FPL databáze</span>
        </div>
    );
}
